import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Award, Home, ChevronRight, Loader2 } from 'lucide-react';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

const API = process.env.REACT_APP_BACKEND_URL;

const AgentWebsite = () => {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/agent-sites/${slug}`);
        setAgent(data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else toast.error('Failed to load agent profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!agent) return;
    (async () => {
      setListingsLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/agent-sites/${slug}/listings?limit=6`);
        setListings(data.listings || []);
      } catch (e) {
        // silent — listings are best-effort
      } finally {
        setListingsLoading(false);
      }
    })();
  }, [agent, slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in name, email, and message');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/agent-sites/${slug}/inquiry`, form);
      toast.success(`Thanks ${form.name.split(' ')[0]}! ${agent.name.split(' ')[0]} will be in touch shortly.`);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f7f3] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#BCA480]" />
      </div>
    );
  }

  if (notFound || !agent) {
    return (
      <div className="min-h-screen bg-[#f9f7f3]">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-[#434345] mb-3">Agent Not Found</h1>
          <p className="text-gray-600 mb-6">This agent profile doesn&apos;t exist or is no longer active.</p>
          <Link
            to="/agents"
            className="inline-block bg-[#BCA480] text-white px-6 py-3 rounded hover:bg-[#a89470] transition-colors"
            data-testid="back-to-agents"
          >
            Back to Our Agents
          </Link>
        </div>
      </div>
    );
  }

  const initials = agent.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#f9f7f3]" data-testid="agent-website-page">
      <StructuredData schema={[
        schemas.person(agent),
        schemas.breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Our Agents', url: '/agents' },
          { name: agent.name, url: `/agents/${agent.slug}` },
        ]),
      ]} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#434345] to-[#2a2a2c] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-[240px_1fr] gap-10 items-center">
            <div className="flex justify-center md:justify-start">
              {agent.photo ? (
                <div className={`relative w-56 h-56 rounded-full border-4 border-[#BCA480] shadow-xl overflow-hidden ${
                  agent.photo_position === 'object-contain-white' ? 'bg-white' : 'bg-[#434345]'
                }`}>
                  {agent.photo_position === 'object-contain' && (
                    <img
                      src={agent.photo}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover scale-125 blur-lg opacity-60"
                    />
                  )}
                  <img
                    src={agent.photo}
                    alt={agent.name}
                    className={`relative w-full h-full ${
                      agent.photo_position === 'object-contain' || agent.photo_position === 'object-contain-white'
                        ? 'object-contain'
                        : `object-cover ${
                            agent.photo_position && agent.photo_position.startsWith('object-[')
                              ? ''
                              : (agent.photo_position || 'object-center')
                          }`
                    }`}
                    style={
                      agent.photo_position && agent.photo_position.startsWith('object-[')
                        ? { objectPosition: agent.photo_position.slice(8, -1).replace(/_/g, ' ') }
                        : undefined
                    }
                    data-testid="agent-photo"
                  />
                </div>
              ) : (
                <div className="w-56 h-56 rounded-full bg-[#BCA480] flex items-center justify-center text-6xl font-bold text-white shadow-xl">
                  {initials}
                </div>
              )}
            </div>
            <div className="text-center md:text-left">
              <p className="text-xs tracking-widest uppercase text-[#BCA480] mb-2">Bespoke Realty</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3" data-testid="agent-name">{agent.name}</h1>
              <p className="text-lg text-gray-200 mb-4" data-testid="agent-title">{agent.title}</p>
              {agent.tagline && (
                <p className="italic text-gray-300 mb-6 max-w-2xl">&ldquo;{agent.tagline}&rdquo;</p>
              )}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                {agent.phone && (
                  <a
                    href={`tel:${agent.phone}`}
                    className="inline-flex items-center gap-2 bg-[#BCA480] text-white px-5 py-3 rounded font-semibold hover:bg-[#a89470] transition-colors"
                    data-testid="agent-hero-phone"
                  >
                    <Phone className="h-4 w-4" /> {agent.phone}
                  </a>
                )}
                {agent.email && (
                  <a
                    href={`mailto:${agent.email}`}
                    className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 rounded font-semibold hover:bg-white/10 transition-colors"
                    data-testid="agent-hero-email"
                  >
                    <Mail className="h-4 w-4" /> Email
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About + Credentials */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[2fr_1fr] gap-12">
          <div>
            <h2 className="text-3xl font-bold text-[#434345] mb-4">About {agent.name.split(' ')[0]}</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{agent.bio || 'Bio coming soon.'}</p>

            {agent.specialties && agent.specialties.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xs tracking-widest uppercase text-[#BCA480] mb-3">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.specialties.map((s) => (
                    <span key={s} className="bg-[#faf8f4] border border-[#BCA480]/40 text-[#434345] px-3 py-1.5 rounded-full text-sm">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="bg-white rounded-lg shadow-sm p-6 h-fit">
            <h3 className="text-xs tracking-widest uppercase text-[#BCA480] mb-4 flex items-center gap-2">
              <Award className="h-4 w-4" /> Credentials
            </h3>
            <div className="space-y-3 text-sm">
              {agent.licenses?.tn && (
                <div className="flex items-start gap-3">
                  <span className="bg-[#BCA480]/20 text-[#434345] px-2 py-0.5 rounded text-xs font-semibold">TN</span>
                  <span className="text-gray-700">License #{agent.licenses.tn}</span>
                </div>
              )}
              {agent.licenses?.ms && (
                <div className="flex items-start gap-3">
                  <span className="bg-[#BCA480]/20 text-[#434345] px-2 py-0.5 rounded text-xs font-semibold">MS</span>
                  <span className="text-gray-700">License #{agent.licenses.ms}</span>
                </div>
              )}
              {agent.licenses?.ar && (
                <div className="flex items-start gap-3">
                  <span className="bg-[#BCA480]/20 text-[#434345] px-2 py-0.5 rounded text-xs font-semibold">AR</span>
                  <span className="text-gray-700">License #{agent.licenses.ar}</span>
                </div>
              )}
            </div>

            {agent.service_areas && agent.service_areas.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-xs tracking-widest uppercase text-[#BCA480] mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Service Areas
                </h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  {agent.service_areas.map((a) => (
                    <li key={a} className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3 text-[#BCA480]" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Listings */}
      <section className="py-16 bg-white" data-testid="agent-listings-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-widest uppercase text-[#BCA480] mb-2">Currently Representing</p>
              <h2 className="text-3xl font-bold text-[#434345]">Featured Listings</h2>
            </div>
            <Link
              to="/mls"
              className="text-sm text-[#BCA480] hover:underline hidden sm:inline-flex items-center gap-1"
              data-testid="see-all-listings"
            >
              Browse All Listings <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {listingsLoading ? (
            <div className="text-center py-12 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#BCA480]" />
              Loading listings...
            </div>
          ) : listings.length === 0 ? (
            <div className="bg-[#faf8f4] border border-[#BCA480]/30 rounded-lg p-8 text-center">
              <Home className="h-10 w-10 mx-auto text-[#BCA480] mb-3" />
              <p className="text-[#434345] font-semibold mb-1">No active listings right now.</p>
              <p className="text-sm text-gray-600 mb-4">Reach out below and {agent.name.split(' ')[0]} will share upcoming listings and off-market opportunities.</p>
              <Link to="/mls" className="inline-block bg-[#BCA480] text-white px-5 py-2 rounded text-sm font-semibold hover:bg-[#a89470] transition-colors">
                Browse the full MLS
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l) => (
                <Link
                  key={l.mlsId}
                  to={`/mls/${l.mlsId}`}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-gray-100"
                  data-testid={`agent-listing-${l.mlsId}`}
                >
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    {l.photos?.[0] ? (
                      <img src={l.photos[0]} alt={l.address?.streetName || 'Listing'} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Home className="h-10 w-10" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-lg font-bold text-[#434345]">${(l.listPrice || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-600 truncate">
                      {l.address?.streetNumber} {l.address?.streetName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {l.address?.city}, {l.address?.state} · {l.property?.bedrooms || 0} bd · {l.property?.bathsFull || 0} ba
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-[#f9f7f3]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-10">
            <p className="text-xs tracking-widest uppercase text-[#BCA480] mb-2 text-center">Get in Touch</p>
            <h2 className="text-3xl font-bold text-[#434345] mb-2 text-center">Work with {agent.name.split(' ')[0]}</h2>
            <p className="text-gray-600 text-center mb-8">
              Have a question about a property, want to schedule a showing, or ready to explore your options?
              Send a quick note — {agent.name.split(' ')[0]} will personally respond.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="agent-inquiry-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded px-4 py-3 focus:border-[#BCA480] focus:outline-none"
                  required
                  data-testid="agent-inquiry-name"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 rounded px-4 py-3 focus:border-[#BCA480] focus:outline-none"
                  required
                  data-testid="agent-inquiry-email"
                />
              </div>
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-gray-200 rounded px-4 py-3 focus:border-[#BCA480] focus:outline-none"
                data-testid="agent-inquiry-phone"
              />
              <textarea
                placeholder="How can I help you? *"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full border border-gray-200 rounded px-4 py-3 focus:border-[#BCA480] focus:outline-none"
                required
                data-testid="agent-inquiry-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#BCA480] text-white py-3 rounded font-semibold hover:bg-[#a89470] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="agent-inquiry-submit"
              >
                {submitting ? 'Sending...' : `Send Message to ${agent.name.split(' ')[0]}`}
              </button>
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                By providing your phone number, you consent to receive transactional SMS from Bespoke Realty
                regarding your inquiry. Message frequency varies. Msg & data rates may apply.
                Reply STOP to opt out, HELP for help. See our{' '}
                <Link to="/privacy" className="text-[#BCA480] hover:underline">Privacy Policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgentWebsite;
